import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import { MessagePopup, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import CogIcon from 'mdi-react/CogIcon'
import PencilIcon from 'mdi-react/PencilIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { UnsavedChangesButton } from '@/components/UnsavedChangesButton'
import { ADMIN_ROLES, useIsAdmin } from '@/hooks/access'
import useUser from '@/hooks/user'
import queries from '@/modules/ecom-policy/queries'
import type { PolicyExtended } from '@/modules/ecom-policy/types'
import { APP_ROUTES } from '@/util/app-routes'
import { validateEmail } from '@/util/formatters/text'

import { usePolicyId } from '../hooks'
import { usePolicyFilters } from '../Policy.context'

import { AdvancedSettings } from './AdvancedSettings'
import Amounts from './Amounts'
import BulletPoints from './BulletPoints'
import Flags from './Flags'
import { GreenpoolSettings } from './GreenpoolSettings'
import { getInitialValues, getPayload } from './helpers'
import { useChangePolicySettings } from './hooks'
import Intervals from './Intervals'
import Name from './Name'
import type { FormFields } from './types'

import ecomPolicyResources from '../../resources'

import './Settings.styles.scss'

const Settings: FC<{ policy: PolicyExtended }> = ({ policy }) => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const queryClient = useQueryClient()

  const role = useUser()?.role
  const policyId = usePolicyId()
  const [, setIsVatIncluded] = usePolicyFilters().isVatIncluded
  const isAdmin = useIsAdmin()
  const lockFields = policy.isLeasing ? policy.lockFields : []

  const formRef = useRef<FormikProps<FormFields>>(null)
  const [isPolicyNameModalOpen, setIsPolicyNameModalOpen] = useState(false)
  const [isAdvancedSettingsModalOpen, setIsAdvancedSettingsModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [isGreenpoolSettingsModalOpen, setIsGreenpoolSettingsModalOpen] = useState(false)

  const { isSaving, changePolicySettings } = useChangePolicySettings({
    onError: () => setIsErrorModalOpen(true),
  })

  const updatePolicyCategoryClassification = useMutation(
    ecomPolicyResources.updatePolicyCategoryClassification,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.policyDetails(policyId))
        queryClient.invalidateQueries(queries.policyProducts(policyId), { refetchType: 'all' })
      },
    }
  )

  useEffect(() => {
    if (role && !ADMIN_ROLES.includes(role) && policy.isAdminRestricted) {
      navigate(APP_ROUTES.HOME)
    }
  }, [navigate, policy.isAdminRestricted, role])

  useEffect(() => {
    setIsVatIncluded(policy.hasVat)
  }, [policy.hasVat, setIsVatIncluded])

  return (
    <span className="policy-settings">
      <button className="edit-name-and-desc" onClick={() => setIsPolicyNameModalOpen(true)}>
        <h2>
          {policy.name} <PencilIcon />
        </h2>

        <h3>{policy.description}</h3>
      </button>

      <BulletPoints />

      <Formik<FormFields>
        enableReinitialize
        innerRef={formRef}
        initialValues={getInitialValues(policy)}
        initialTouched={{
          classificationList: true,
        }}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string>> = {}

          if (values.greenpoolTypeId && !values.greenpoolNotificationEmail) {
            errors.greenpoolNotificationEmail = translate('This field is mandatory')
          }
          if (
            values.greenpoolNotificationEmail &&
            !validateEmail(values.greenpoolNotificationEmail)
          ) {
            errors.greenpoolNotificationEmail = translate('Invalid email format')
          }
          if (values.greenpoolTypeId === 'OPTIONAL' && !values.greenpoolSplashText) {
            errors.greenpoolSplashText = translate('This field is mandatory')
          }

          if (values.greenpoolTypeId && !values.greenpoolConfirmationText) {
            errors.greenpoolConfirmationText = translate('This field is mandatory')
          }

          if (!values.classificationList || values.classificationList.length === 0) {
            errors.classificationList = translate('At least one value must be selected')
          }

          return errors
        }}
        onSubmit={formValues => {
          if (!formRef.current?.dirty) return

          updatePolicyCategoryClassification.mutate({
            policyId: policy.id,
            policyCategoryClassificationList: formValues.classificationList,
          })

          changePolicySettings({
            policyId,
            payload: getPayload(formValues, isAdmin),
          })
        }}
      >
        {({ errors, dirty, isValid, setFieldTouched, handleSubmit }) => (
          <Form>
            <div className="input-cards">
              <Amounts />
              <Intervals
                hideOrder={lockFields?.includes('order_interval')}
                hideCover={lockFields?.includes('cover_interval')}
              />

              <Flags
                isActive={policy.isActive}
                customerId={policy.customerId}
                hideBuyout={lockFields?.includes('is_buyout_allowed')}
              />
            </div>

            <Popup
              isOpen={isPolicyNameModalOpen}
              ariaLabel={translate('Policy name')}
              onClose={() => {
                /* the pop does not submit the values selected so before closing it is being checked that everything is fine
                 * and if not can not be closed without resolving the problems
                 */
                if (errors.classificationList) return
                setIsPolicyNameModalOpen(false)
              }}
            >
              <Name />
            </Popup>

            <Popup
              ariaLabel={translate('Advanced policy settings')}
              isOpen={isAdvancedSettingsModalOpen}
              onClose={() => setIsAdvancedSettingsModalOpen(false)}
            >
              <AdvancedSettings />
            </Popup>

            <Popup
              ariaLabel={translate('Greenpool settings')}
              isOpen={isGreenpoolSettingsModalOpen}
              onClose={() => {
                if (
                  errors.greenpoolNotificationEmail ||
                  errors.greenpoolSplashText ||
                  errors.greenpoolConfirmationText
                ) {
                  setFieldTouched('greenpoolNotificationEmail', true)
                  setFieldTouched('greenpoolSplashText', true)
                  setFieldTouched('greenpoolConfirmationText', true)
                  return
                }
                setIsGreenpoolSettingsModalOpen(false)
              }}
            >
              <GreenpoolSettings />
            </Popup>

            <footer>
              <Button
                className={twJoin(
                  (errors.greenpoolConfirmationText ||
                    errors.greenpoolSplashText ||
                    errors.greenpoolNotificationEmail) &&
                    'border-red bg-red border-2 border-solid bg-opacity-10'
                )}
                Icon={<RecycleIcon className="fill-green-500" />}
                onClick={() => setIsGreenpoolSettingsModalOpen(true)}
              >
                <Translation id="Greenpool settings" />
              </Button>

              {isAdmin && (
                <Button Icon={<CogIcon />} onClick={() => setIsAdvancedSettingsModalOpen(true)}>
                  <Translation id="Advanced settings" />
                </Button>
              )}

              <UnsavedChangesButton
                hasUnsavedChanges={dirty}
                disabled={isSaving || updatePolicyCategoryClassification.isLoading}
                error={!isValid}
                isLoading={isSaving || updatePolicyCategoryClassification.isLoading}
                onClick={handleSubmit}
              />
            </footer>
          </Form>
        )}
      </Formik>

      <MessagePopup
        title={<Translation id="Saving settings error" />}
        message={<Translation id="Ooops! Something went wrong. Please contact our support." />}
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </span>
  )
}

export default Settings
